/**
 * Datatype glyphs for action return types (public/images/action-icons), the same mapping family as
 * generic-sql-panel's getTypeIcon. Keys are lower-cased type names; an unknown or custom type has no
 * entry and the caller falls back to the common info-circle, with the exact type name beside it.
 */
export const ACTION_RETURN_TYPE_ICONS: Record<string, string> = {
  string: "string",
  boolean: "boolean",
  bool: "boolean",
  int16: "int16",
  int32: "int32",
  int: "int32",
  int64: "int64",
  bigint: "int64",
  long: "int64",
  double: "double",
  float: "double",
  single: "double",
  decimal: "decimal",
  byte: "decimal",
  date: "date&time",
  datetime: "date&time",
  datetimeoffset: "datetimeoffset",
  guid: "GUID",
  location: "location",
  filecontent: "file-content"
};

/** The action-icons glyph for a return type, or undefined when the type has none of its own. */
export function getActionReturnTypeIconName(returnTypeName: string | undefined): string | undefined {
  if (!returnTypeName) {
    return undefined;
  }
  return ACTION_RETURN_TYPE_ICONS[returnTypeName.toLowerCase()];
}

/** True when an action returns nothing worth showing: no type at all, or `void`. */
export function isVoidReturnType(returnTypeName: string | undefined): boolean {
  return !returnTypeName || returnTypeName.toLowerCase() === "void";
}

/**
 * Where the catalogue's descriptions stop describing the action and start listing its signature.
 * They are written for the MCP tools as much as for people ("Get entities. Parameters: categoryId, …
 * Returns: KEntityModel."), and the preview already draws parameters and the return type from the
 * definition itself — repeating them as prose would say everything twice.
 */
const SIGNATURE_MARKERS: readonly RegExp[] = [
  /(?:\b(?:with|using|and)\s+)?(?:\bits\s+)?\bparameters(?:\s+are)?\s*:/i,
  /\breturns\s*:/i
];

/**
 * A description short enough to read at a glance: the prose before the signature list, cut to its
 * first sentence when that sentence stands on its own. Returns "" when there is nothing to say.
 */
export function summarizeActionDescription(description: string | undefined | null): string {
  if (!description) {
    return "";
  }
  // Some catalogue texts open with an internal grouping tag ("[Dashboard] Returns …"); it names the
  // subcategory the breadcrumb already shows.
  let text: string = description.trim().replace(/^\[[^\]]*\]\s*/, "");
  for (const marker of SIGNATURE_MARKERS) {
    const match: RegExpExecArray | null = marker.exec(text);
    if (match && match.index > 0) {
      text = text.substring(0, match.index).trim();
    }
  }
  // First sentence only — a period followed by whitespace and a capital, so "e.g. docx" and a
  // version number such as "1.5" do not end it early.
  const sentenceEnd: RegExpExecArray | null = /[.!?](?=\s+[A-Z])/.exec(text);
  if (sentenceEnd) {
    text = text.substring(0, sentenceEnd.index + 1);
  }
  // A cut can leave the sentence hanging on the word that introduced the list ("… with").
  return text
    .replace(/[\s,;:–-]+$/, "")
    .replace(/\s+(?:with|using|and|by|of|the|its)$/i, "")
    .trim();
}
